<template>
  <div class="ccma-contact-details">
    <!-- Full-screen loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
        <p class="loading-text">{{ t('consultantCorner.contactDetails.LoadingContactDetails') }}</p>
      </div>
    </div>

    <div v-if="showError" class="ccma-contact-details-error">
      <h2>{{ t('consultantCorner.contactDetails.PageLoadError') }}</h2>
    </div>

    <!-- Error notification for save operations -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.contactDetails.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="saveContactInfo">{{ t('consultantCorner.contactDetails.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.contactDetails.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>
    <div v-else-if="!showError && !isLoading">
      <!-- Header Section -->
      <div class="contact-header">
        <div class="first">
          <div class="contact-title">{{ t('consultantCorner.contactDetails.ContactDetails') }}</div>
          <div class="contact-subtitle mb-1">{{ t('consultantCorner.contactDetails.UpdateContactInfo') }}</div>
        </div>
        <div class="contact-buttons">

          <button
            id="btnSave"
            class="baseBtn primaryBtn"
            type="button"
            @click="saveContactInfo"
            :disabled="isSaving"
          >
            {{ isSaving ? t('consultantCorner.contactDetails.Saving') : t('consultantCorner.contactDetails.SaveContactInfo') }}
          </button>
        </div>
      </div>

      <!-- Contact Details Form -->
      <div class="contact-form-container">
        <div class="contact-form-content">
          <form @submit.prevent="saveContactInfo">
            <fieldset class="k-form-fieldset">

              <!-- Username Display (Read-only) -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.contactDetails.AccountInfo') }}</h3>
                <div class="form-row">
                  <div class="form-field">
                    <label class="form-label">{{ t('consultantCorner.contactDetails.Username') }}</label>
                    <div class="readonly-field">{{ profileData.beeNumber || '-' }}</div>
                  </div>
                </div>
              </div>

              <!-- Name Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.contactDetails.NameSection') }}</h3>
                <div class="form-row">
                  <div class="form-field">
                    <label class="form-label required">{{ t('consultantCorner.contactDetails.FirstName') }}</label>
                    <input
                      v-model="profileData.firstName"
                      type="text"
                      class="form-input"
                      :class="{ 'error': errors.firstName }"
                      required
                      @input="validateFirstName"
                      @blur="validateFirstName"
                    />
                    <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
                  </div>
                  <div class="form-field">
                    <label class="form-label required">{{ t('consultantCorner.contactDetails.LastName') }}</label>
                    <input
                      v-model="profileData.lastName"
                      type="text"
                      class="form-input"
                      :class="{ 'error': errors.lastName }"
                      required
                      @input="validateLastName"
                      @blur="validateLastName"
                    />
                    <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
                  </div>
                </div>
              </div>

              <!-- Contact Information Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.contactDetails.ContactInfo') }}</h3>

                <!-- Email -->
                <div class="form-row">
                  <div class="form-field full-width">
                    <label class="form-label required">{{ t('consultantCorner.contactDetails.EmailAddress') }}</label>
                    <input
                      v-model="profileData.emailAddress"
                      type="email"
                      class="form-input"
                      :class="{ 'error': errors.emailAddress }"
                      required
                      @input="validateEmailAddress"
                      @blur="validateEmailAddress"
                    />
                    <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
                  </div>
                </div>

                <!-- Primary Phone -->
                <div class="form-row">
                  <div class="form-field">
                    <label class="form-label">{{ t('consultantCorner.contactDetails.PrimaryPhone') }}</label>
                    <PhoneInputControl
                      ref="primaryPhoneControl"
                      v-model="primaryPhoneModel"
                      :required="false"
                      :showExtension="false"
                      :showPhoneType="false"
                      :countryList="phoneCountryOptions"
                      :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                    />
                  </div>
                  <div class="form-field">
                    <label class="form-label">{{ t('consultantCorner.contactDetails.MobilePhone') }}</label>
                    <PhoneInputControl
                      ref="altPhone1Control"
                      v-model="altPhone1Model"
                      :required="false"
                      :showExtension="false"
                      :showPhoneType="false"
                      :countryList="phoneCountryOptions"
                      :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                    />
                  </div>
                </div>
              </div>

            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { PwsProfileGeneralGet, PwsProfileGeneralSave, type PwsProfileGeneralViewModel } from '../../api/ConsultantCorner'
import { useStoreLogger } from '../../logger'
import { Loader } from '@progress/kendo-vue-indicators'
import PhoneInputControl from '../PhoneInputControl.vue'
import type { IPhoneOutput } from '../../types/CommonTypes'

const { t } = useI18n()
const logger = useStoreLogger()

// Reactive state
const showError = ref(false)
const isSaving = ref(false)
const saveError = ref(false)
const saveErrorMessage = ref('')
const saveSuccess = ref(false)
const isLoading = ref(false)

// Phone input refs for validation
const primaryPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const altPhone1Control = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Phone models for PhoneInputControl (IPhoneOutput format)
const primaryPhoneModel = ref<IPhoneOutput>({
  number: '',
  countryCode: '',
  codeIdPhoneType: '',
  extension: null,
  formattedNumber: ''
})
const altPhone1Model = ref<IPhoneOutput>({
  number: '',
  countryCode: '',
  codeIdPhoneType: '',
  extension: null,
  formattedNumber: ''
})

// Country options for phone input (limited list)
const phoneCountryOptions = [
  { text: 'DE', id: 'DE' },
  { text: 'AT', id: 'AT' },
  { text: 'FR', id: 'FR' }
]

const profileData = reactive<PwsProfileGeneralViewModel>({
  beeNumber: undefined,
  firstName: '',
  lastName: '',
  preferredName: '',
  alternateFirstName: '',
  alternateLastName: '',
  alternatePreferredName: '',
  timeZone: '',
  emailAddress: '',
  primaryPhone: {
    countryCode: '',
    phoneNumber: '',
    phoneType: ''
  },
  altPhone1: {
    countryCode: '',
    phoneNumber: '',
    phoneType: ''
  },
  altPhone2: {
    countryCode: '',
    phoneNumber: '',
    phoneType: ''
  },
  altPhone3: {
    countryCode: '',
    phoneNumber: '',
    phoneType: ''
  }
})

const errors = reactive({
  firstName: '',
  lastName: '',
  emailAddress: ''
})

// Methods
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

const validateFirstName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!profileData.firstName.trim()) {
    errors.firstName = t('consultantCorner.contactDetails.FirstNameRequired')
    return false
  } else if (!nameRegex.test(profileData.firstName)) {
    errors.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    return false
  } else if (profileData.firstName.trim().length < 2) {
    errors.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    return false
  } else if (profileData.firstName.length > 50) {
    errors.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    return false
  }

  errors.firstName = ''
  return true
}

const validateLastName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!profileData.lastName.trim()) {
    errors.lastName = t('consultantCorner.contactDetails.LastNameRequired')
    return false
  } else if (!nameRegex.test(profileData.lastName)) {
    errors.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    return false
  } else if (profileData.lastName.trim().length < 2) {
    errors.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    return false
  } else if (profileData.lastName.length > 50) {
    errors.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    return false
  }

  errors.lastName = ''
  return true
}

const validateEmailAddress = (): boolean => {
  if (!profileData.emailAddress.trim()) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailRequired')
    return false
  } else if (!validateEmail(profileData.emailAddress)) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailInvalid')
    return false
  }

  errors.emailAddress = ''
  return true
}

const validateForm = (): boolean => {
  // Clear previous errors
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })

  let isValid = true

  // Validate individual fields
  if (!validateFirstName()) isValid = false
  if (!validateLastName()) isValid = false
  if (!validateEmailAddress()) isValid = false

  // Validate phone numbers using PhoneInputControl's validation
  // Only validate if there's a phone number entered (phones are optional)
  if (primaryPhoneModel.value.number && primaryPhoneControl.value) {
    if (!primaryPhoneControl.value.validate()) {
      isValid = false
    }
  }

  if (altPhone1Model.value.number && altPhone1Control.value) {
    if (!altPhone1Control.value.validate()) {
      isValid = false
    }
  }

  return isValid
}

const loadProfileData = async () => {
  try {
    isLoading.value = true
    showError.value = false
    // Get beeNumber from user context or route params
    const beeNumber = 123456 // This should come from user context/store

    const response = await PwsProfileGeneralGet()

    // Map the API response to the form structure
    const item = response.item
    profileData.beeNumber = item.beeNumber
    profileData.firstName = item.firstName || ''
    profileData.lastName = item.lastName || ''
    profileData.preferredName = item.preferredName || ''
    profileData.alternateFirstName = item.alternateFirstName || ''
    profileData.alternateLastName = item.alternateLastName || ''
    profileData.alternatePreferredName = item.alternatePreferredName || ''
    profileData.timeZone = item.timeZone || ''
    profileData.emailAddress = item.emailAddress || ''

    // Map phone numbers to PhoneInputControl models (IPhoneOutput format)
    if (item.primaryPhone) {
      primaryPhoneModel.value = {
        number: (item.primaryPhone as any).number || '',
        countryCode: item.primaryPhone.countryCode || '',
        codeIdPhoneType: (item.primaryPhone as any).codeIdPhoneType || '',
        extension: null,
        formattedNumber: (item.primaryPhone as any).formattedNumber || ''
      }
    }

    if (item.altPhone1) {
      altPhone1Model.value = {
        number: (item.altPhone1 as any).number || '',
        countryCode: item.altPhone1.countryCode || '',
        codeIdPhoneType: (item.altPhone1 as any).codeIdPhoneType || '',
        extension: null,
        formattedNumber: (item.altPhone1 as any).formattedNumber || ''
      }
    }

    if (item.altPhone2) {
      profileData.altPhone2 = {
        ...item.altPhone2,
        phoneNumber: (item.altPhone2 as any).number || '',
        countryCode: item.altPhone2.countryCode || '',
        phoneType: (item.altPhone2 as any).codeIdPhoneType || '',
        codeIdPhoneType: (item.altPhone2 as any).codeIdPhoneType,
        number: (item.altPhone2 as any).number,
        formattedNumber: (item.altPhone2 as any).formattedNumber
      }
    }

    if (item.altPhone3) {
      profileData.altPhone3 = {
        ...item.altPhone3,
        phoneNumber: (item.altPhone3 as any).number || '',
        countryCode: item.altPhone3.countryCode || '',
        phoneType: (item.altPhone3 as any).codeIdPhoneType || '',
        codeIdPhoneType: (item.altPhone3 as any).codeIdPhoneType,
        number: (item.altPhone3 as any).number,
        formattedNumber: (item.altPhone3 as any).formattedNumber
      }
    }

    logger.info('Profile data loaded successfully', response)
  } catch (error) {
    logger.error('Error loading profile data:', error)
    showError.value = true
  } finally {
    isLoading.value = false
  }
}

const saveContactInfo = async () => {
  if (!validateForm()) {
    saveErrorMessage.value = t('consultantCorner.contactDetails.ValidationError')
    saveError.value = true
    return
  }

  try {
    isSaving.value = true
    clearNotifications()

    // Prepare the data for saving - update phone numbers from form to API format
    const saveData = {
      ...profileData,
      primaryPhone: {
        ...profileData.primaryPhone,
        number: primaryPhoneModel.value.number,
        countryCode: !!primaryPhoneModel.value.number ? primaryPhoneModel.value.countryCode : '',
        codeIdPhoneType: !!primaryPhoneModel.value.number ? primaryPhoneModel.value.codeIdPhoneType : '',
        formattedNumber: !!primaryPhoneModel.value.number ? primaryPhoneModel.value.formattedNumber : ''
      },
      altPhone1: {
        ...profileData.altPhone1,
        number: altPhone1Model.value.number,
        countryCode: !!altPhone1Model.value.number ? altPhone1Model.value.countryCode : '',
        codeIdPhoneType: !!altPhone1Model.value.number ? altPhone1Model.value.codeIdPhoneType : '',
        formattedNumber: !!altPhone1Model.value.number ? altPhone1Model.value.formattedNumber : ''
      },
      altPhone2: {
        ...profileData.altPhone2,
        number: profileData.altPhone2.phoneNumber // Update number field from form
      },
      altPhone3: {
        ...profileData.altPhone3,
        number: profileData.altPhone3.phoneNumber // Update number field from form
      }
    }

    const response = await PwsProfileGeneralSave(saveData)
    // Check for ServiceStack ResponseStatus errors
    if (response && response.responseStatus) {
      const responseStatus = response.responseStatus

      // Handle specific error types
      if (responseStatus.errorCode || responseStatus.message) {
        const errorMessage = responseStatus.message || t('consultantCorner.contactDetails.SaveErrorGeneric')

        logger.error('ServiceStack error saving contact information:', {
          errorCode: responseStatus.errorCode,
          message: responseStatus.message,
          errors: responseStatus.errors
        })

        // Show user-friendly error message
        saveErrorMessage.value = getUserFriendlyErrorMessage(responseStatus)
        saveError.value = true
        return
      }
    }

    logger.info('Contact information saved successfully')

    // Show success notification
    saveSuccess.value = true

    // Auto-hide success message and refresh data after 2 seconds
    setTimeout(() => {
      clearNotifications()
      loadProfileData() // Refresh the profile data instead of going back
    }, 2000)

  } catch (error: any) {
    logger.error('Error saving contact information:', error)

    // Handle different types of errors
    if (error.response) {
      // HTTP error response
      const status = error.response.status
      const data = error.response.data

      if (data && data.ResponseStatus) {
        // ServiceStack error in HTTP response
        saveErrorMessage.value = getUserFriendlyErrorMessage(data.ResponseStatus)
      } else if (status >= 500) {
        saveErrorMessage.value = t('consultantCorner.contactDetails.SaveErrorGeneric')
      } else if (status === 400) {
        saveErrorMessage.value = t('consultantCorner.contactDetails.ValidationError')
      } else {
        saveErrorMessage.value = `${t('consultantCorner.contactDetails.SaveError')}: ${status}`
      }
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      // Network error
      saveErrorMessage.value = t('consultantCorner.contactDetails.NetworkError')
    } else {
      // Generic error
      saveErrorMessage.value = error.message || t('consultantCorner.contactDetails.SaveErrorGeneric')
    }

    saveError.value = true
  } finally {
    isSaving.value = false
  }
}

const getUserFriendlyErrorMessage = (responseStatus: any): string => {
  // Map common ServiceStack error codes to user-friendly messages
  const errorCode = responseStatus.ErrorCode
  const message = responseStatus.Message

  // Handle specific error codes
  switch (errorCode) {
    case 'ValidationException':
    case 'ArgumentException':
      return t('consultantCorner.contactDetails.ValidationError')
    case 'UnauthorizedAccessException':
    case 'AuthenticationException':
      return 'You are not authorized to perform this action. Please log in again.'
    case 'TimeoutException':
      return 'The request timed out. Please try again.'
    case 'SqlException':
    case 'DatabaseException':
      return 'A database error occurred. Please try again later.'
    default:
      // Use the message from the server if available, otherwise use generic message
      return message || t('consultantCorner.contactDetails.SaveErrorGeneric')
  }
}

const clearNotifications = () => {
  saveError.value = false
  saveSuccess.value = false
  saveErrorMessage.value = ''
}

const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}

// Lifecycle
onMounted(async () => {
  loadProfileData()
})
</script>

<style lang="scss" scoped>
.ccma-contact-details {
  padding: 1.5rem;
  width: 100%;
  position: relative;
}

.ccma-contact-details-error {
  text-align: center;
  padding: 2rem;
  color: #dc3545;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-text {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #666;
}

/* Error notification styles */
.save-error-notification {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.error-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  gap: 0.75rem;
}

.error-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.error-details {
  flex: 1;
}

.error-details h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
  color: #721c24;
}

.error-details p {
  @include typography-body;
  margin: 0;
  color: #721c24;
}

.error-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.error-close:hover {
  background-color: rgba(#721c24, 0.1);
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #f5c6cb;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

/* Success notification styles */
.save-success-notification {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
}

.success-icon {
  @include typography-h4;
  flex-shrink: 0;
}

.success-message {
  flex: 1;
}

.success-message p {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #155724;
}

.success-close {
  @include typography-h4;
  background: none;
  border: none;
  color: #155724;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.15s ease;
}

.success-close:hover {
  background-color: rgba(#155724, 0.1);
}

/* Animation for notifications */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.cc-back-button {
  background: none;
  @include typography-body;
  border: none;

  cursor: pointer;
  margin-bottom: 0.5rem;
}

.cc-back-button:hover {
  text-decoration: underline;
}

.contact-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.25rem;
}

.contact-subtitle {
  @include typography-body;
  color: #6c757d;
}

.contact-buttons {
  display: flex;
  gap: 0.75rem;
}

.contact-form-container {
  background: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  width: 100%;
}

.contact-form-content {
  padding: 2rem;
  width: 100%;
  max-width: none;
}

.form-section {
  margin-bottom: 2rem;
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevents flex items from overflowing */
}

.form-field.full-width {
  flex: 1 1 100%;
  width: 100%;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-input:focus {
  outline: none;

  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.readonly-field {
  padding: 0.75rem;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  @include typography-body;
  border-radius: $radius-sm;
  color: #6c757d;
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

.baseBtn {
  padding: 0.5rem 1.5rem;
  @include typography-body;
  @include font-weight-medium;
  border: none;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.baseBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .ccma-contact-details {
    padding: 1rem;
  }

  .contact-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .contact-buttons {
    margin-top: 0;
    justify-content: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }

  .contact-buttons .baseBtn {
    width: 100%;
  }

  .form-row {
    flex-direction: column;
    gap: 1rem;
  }

  .contact-form-content {
    padding: 1.5rem 1rem;
  }

  .form-field {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .ccma-contact-details {
    padding: 0.5rem;
  }

  .contact-form-content {
    padding: 1rem;
  }

  .form-row {
    gap: 0.75rem;
  }
}
</style>
